import React from 'react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
  style?: React.CSSProperties
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  style = {}
}: PaginationProps) {
  // Calcular informações da paginação
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)
  
  // Gerar array de páginas para mostrar
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    
    if (totalPages <= maxVisiblePages) {
      // Se há poucas páginas, mostrar todas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Se há muitas páginas, mostrar algumas com "..." no meio
      if (currentPage <= 3) {
        // Páginas iniciais
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        // Páginas finais
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // Páginas do meio
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      }
    }
    
    return pages
  }

  if (totalPages <= 1) return null

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '2rem',
      ...style
    }}>
      {/* Informações da paginação */}
      <div style={{
        fontSize: '0.9rem',
        color: '#666',
        textAlign: 'center'
      }}>
        Mostrando {startItem} a {endItem} de {totalItems} {totalItems === 1 ? 'notícia' : 'notícias'}
      </div>

      {/* Controles de paginação */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        {/* Botão Anterior */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            border: '1px solid #e0e0e0',
            backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
            color: currentPage === 1 ? '#999' : '#333',
            borderRadius: '4px',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            transition: 'all 0.2s ease'
          }}
        >
          ← Anterior
        </button>

        {/* Números das páginas */}
        <div style={{
          display: 'flex',
          gap: '0.25rem'
        }}>
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' ? onPageChange(page) : null}
              disabled={typeof page !== 'number'}
              style={{
                padding: '8px 12px',
                border: '1px solid #e0e0e0',
                backgroundColor: page === currentPage ? '#0072BB' : 'white',
                color: page === currentPage ? 'white' : typeof page === 'number' ? '#333' : '#999',
                borderRadius: '4px',
                cursor: typeof page === 'number' ? 'pointer' : 'default',
                fontSize: '0.9rem',
                minWidth: '40px',
                transition: 'all 0.2s ease'
              }}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Botão Próximo */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 12px',
            border: '1px solid #e0e0e0',
            backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
            color: currentPage === totalPages ? '#999' : '#333',
            borderRadius: '4px',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            transition: 'all 0.2s ease'
          }}
        >
          Próximo →
        </button>
      </div>
    </div>
  )
} 